<template>
    <section
        v-if="!isLayout"
        :id="block.id"
        :data-theme="colorTheme"
        :data-mode="mode"
        class="relative w-full mx-auto surface-background"
        :class="[
            spacing,
            padding,
            {
                [radius]: !blockSettings?.FullWidthBackground,
                [size]: !blockSettings?.FullWidthBackground,
                'overflow-hidden': blockSettings.FullWidthBackground || blockSettings.ContainerWidth === 'full-width',
                'last-of-type:mb-20': blockSettings.ContainerWidth !== 'full-width' && !blockSettings?.FullWidthBackground && !isNewsletterArticle,
            }

        ]"
    >
        <component
            :is="blockComponent"
            v-if="block.canonicalUid"
            :id="block.id"
            :block="blockSettings"
            class="mx-auto"
            :class="[
                size,
            ]"
        />
    </section>

    <!-- minimal layout part -->
    <section
        v-else
        :id="block.id"
        :data-theme="colorTheme"
        :data-mode="mode"
        class="relative w-full"
        :class="[
            layoutHeight,
            layoutContentAlignment,
            layoutPadding,
            {
                'surface-background': !['TileLayoutBlock', 'NewsLayoutBlock', 'EventsLayoutBlock'].includes(componentName),
            }
        ]"
        :style="customLayoutHeight"
    >
        <component
            :is="blockComponent"
            v-if="block.canonicalUid"
            :id="block.id"
            :block="blockSettings"
            :is-singleton="isSingleton"
        />

    </section>
</template>

<script setup>
    import { hydrateOnVisible } from 'vue'

    const props = defineProps({
        block: Object,
        isLayout: Boolean,
        isSingleton: Boolean,
        isNewsletterArticle: Boolean,
    })

    const componentName = computed(() => {
        let name = props.block.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        console.log('componentName', name)

        return name
    })

    const blockSettings = computed(() => {
        return mapKeys(props.block, (value, key) => {
            // remove prefix from key e.g. table_BgTitleOnly => BgTitleOnly
            return replace(key, /.*_/, '')
        })
    })

    const blockComponent = defineAsyncComponent({
        loader: () => import(`~/components/blocks/${upperFirst(componentName.value)}/block.async.vue`),
        hydrate: hydrateOnVisible({ rootMargin: '100px' })
    })

    const colorTheme = computed(() => {
        return blockSettings.value.ColorTheme
    })

    const mode = computed(() => {
        return blockSettings.value.ColorPalette
    })

    const spacing = computed(() => {
        if (props.isNewsletterArticle) {
            return ''
        }

        switch (blockSettings.value.TopSpacing) {
            case 'lg':
                return 'mt-16 lg:mt-32'
            case 'md':
            default:
                return 'mt-12 lg:mt-20'
            case 'sm':
                return 'mt-4 lg:mt-8'
            case 'none':
                return 'mt-0'
        }
    })

    const size = computed(() => {
        // newsletter articles are always full width
        if (props.isNewsletterArticle) {
            return ''
        }

        switch (blockSettings.value?.ContainerWidth) {
            case 'full-width':
                return ''
            case 'wide':
                return 'container-wide'
            case 'extra-wide':
                return 'container-extra-wide'
            default:
                return 'container'
        }
    })

    const layoutHeight = computed(() => {
        switch (blockSettings.value?.Height) {
            case 'auto':
            case 'custom':
            default:
                return 'h-full'
            case 'portrait':
                return 'h-[320px] lg:h-[360px]'
            case 'square':
                return 'h-[250px] lg:h-auto lg:aspect-square'
        }
    })

    const customLayoutHeight = computed(() => {
        if (blockSettings.value?.Height !== 'custom') {
            return ''
        }

        return {
            height: `${blockSettings.value?.CustomHeight}px`
        }
    })

    const layoutContentAlignment = computed(() => {
        switch (blockSettings.value?.ContentAlignment) {
            case 'center':
            default:
                return 'place-content-center'
            case 'top':
                return 'place-content-start'
            case 'bottom':
                return 'place-content-end'

        }
    })

    const layoutPadding = computed(() => {
        switch (blockSettings.value?.Padding) {
            case 'none':
            default:
                return ''
            case 'xs':
                return 'p-4'
            case 'sm':
                return 'p-6'
            case 'md':
                return 'p-10'
            case 'lg':
                return 'p-[60px]'
            case 'xl':
                return 'p-20'

        }
    })

    const padding = computed(() => {

        if (blockSettings.value.FullWidthBackground) {
            return 'px-4 lg:px-8 py-6 lg:py-24'
        }

        if (blockSettings.value.ContainerWidth === 'full-width') {
            return ''
        }

        switch (blockSettings.value.ColorPalette) {
            case 'base':
            case '':
            case undefined:
                return 'px-4 lg:px-0'
            default:
                return 'px-4 py-8 lg:px-16 lg:py-16'
        }
    })

    const radius = computed(() => {
        switch (blockSettings.value.ContainerWidth) {
            case 'full-width':
                return ''
            default:
                return 'lg:radius-card-lg'
        }
    })


</script>

<style lang="scss" scoped>

</style>
